<!--
  Component: Paywall purchase dialog
  Mounted in: App.vue (single global instance)
  Triggered by: useEntitlementStore().openPaywall()
  Design:    docs/screens/purchase-dialog.png

  Two products are presented side-by-side: a one-time non-consumable
  (`lingotalk_lifetime`) at €99.99 and an auto-renewing subscription
  (`lingotalk_monthly`) at €14.99/month. Apple Guideline 3.1.2 requires a
  Restore Purchases control plus visible Privacy + Terms links — the restore
  control is the prominent outline button below the product columns; the
  legal links sit in a small footer row at the very bottom.

  The dialog is intentionally non-`closeable` when the trial is expired and
  the patient has no active entitlement (i.e. they got here by tapping a
  locked feature) — closing the modal would hand control back to a screen
  they can't actually use. When opened during an active trial (via the
  "Preise" link) it is closeable.

  Layout note: do not wrap the two product columns in `<Section variant="duo">`.
  That section variant is sized for a full-page layout (100dvw / 100dvh) and
  blows out of the 700px dialog. The local `.products` grid below is the
  in-dialog equivalent — two equal columns with a vertical divider that
  collapses to a stack on narrow widths.
-->
<template>
  <Dialog
    v-model:show="entitlement.paywallOpen"
    variant="medium"
    :blur="true"
    :closeable="entitlement.isUnlocked || entitlement.isTrial"
  >
    <h1 class="title">{{ t('title') }}</h1>
    <p class="subtitle">{{ t('subtitle') }}</p>

    <div class="products">
      <div class="option">
        <h2 class="option-name">{{ t('lifetime-name') }}</h2>
        <p class="price">{{ priceFor('lingotalk_lifetime') ?? '99,99€' }}</p>
        <p class="cadence">{{ t('lifetime-cadence') }}</p>
        <Button
          variant="next"
          :next-label="t('lifetime-action')"
          :disabled="entitlement.purchasing"
          @click="onPurchase('lingotalk_lifetime')"
        />
      </div>
      <div class="option">
        <h2 class="option-name">{{ t('monthly-name') }}</h2>
        <p class="price">{{ priceFor('lingotalk_monthly') ?? '14,99€' }}</p>
        <p class="cadence">{{ t('monthly-cadence') }}</p>
        <p class="cadence note">{{ t('monthly-note') }}</p>
        <Button
          variant="next"
          :next-label="t('monthly-action')"
          :disabled="entitlement.purchasing"
          @click="onPurchase('lingotalk_monthly')"
        />
      </div>
    </div>

    <p v-if="entitlement.lastError" class="error" role="alert">{{ entitlement.lastError }}</p>

    <h2 class="restore-prompt">{{ t('restore-prompt') }}</h2>
    <div class="restore-row">
      <button
        type="button"
        class="restore"
        :disabled="entitlement.purchasing"
        @click="onRestore"
      >
        {{ t('restore-action') }}
      </button>
    </div>

    <p class="legal">
      <button type="button" class="legal-link" @click="openPrivacy">{{ t('privacy') }}</button>
      <span class="dot">·</span>
      <button type="button" class="legal-link" @click="openTerms">{{ t('terms') }}</button>
    </p>
  </Dialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Browser } from '@capacitor/browser'

import Button from '@/components/Button.vue'
import Dialog from '@/components/Dialog.vue'

import { useEntitlementStore } from '@/stores/entitlement'
import { isNative } from '@/lib/platform'
import { getProducts, initIap, type IapProductInfo, type ProductId } from '@/lib/iap'

const { t } = useI18n()
const entitlement = useEntitlementStore()

// Legal links share the sign-up screen's env vars (LaunchTerms.vue) so the
// paywall and onboarding can never drift to different URLs.
const privacyUrl = import.meta.env.VITE_LEGAL_PRIVACY_URL ?? ''
const termsUrl = import.meta.env.VITE_LEGAL_AGB_URL ?? ''

// Localized prices from the store (when available). We keep the design's
// fallback values ("99,99€" / "14,99€") so the dialog reads correctly on
// web during dev too.
//
// This runs at app start (the dialog is mounted globally), which makes it
// the launch-time initialization of the IAP plugin. That is allowed only
// because `initIap()` + `getProducts()` are silent: they load prices and
// read local purchase state, and reach no payment sheet, no Apple Account
// prompt, no Play recovery card and never open this dialog. Policy in
// CLAUDE.md, "No payment-system UI at launch, ever" — do not add a restore,
// an order or anything else to this hook.
const products = ref<IapProductInfo[]>([])

onMounted(async () => {
  if (!isNative) return
  try {
    await initIap()
    products.value = await getProducts()
  } catch {
    // Plugin not yet ready or running on web — stay on hardcoded prices.
  }
})

function priceFor(id: ProductId): string | null {
  return products.value.find((p) => p.id === id)?.price ?? null
}

async function onPurchase(productId: ProductId): Promise<void> {
  if (!isNative) {
    // Surface a friendly message on web instead of throwing.
    entitlement.lastError = t('not-available-on-web')
    return
  }
  try {
    await entitlement.purchase(productId)
  } catch {
    // The store has populated `lastError`; keep the dialog open.
  }
}

async function onRestore(): Promise<void> {
  if (!isNative) {
    entitlement.lastError = t('not-available-on-web')
    return
  }
  try {
    await entitlement.restore()
  } catch {
    // lastError already populated.
  }
}

async function openPrivacy(): Promise<void> {
  await Browser.open({ url: privacyUrl })
}
async function openTerms(): Promise<void> {
  await Browser.open({ url: termsUrl })
}
</script>

<style scoped lang="css">
/* Dialog's :slotted(h1) rule already gives us font-size 1.25rem / weight 700;
   we only need to recenter and add breathing room above the subtitle. */
h1.title {
  text-align: center;
  margin-top: 0;
  margin-bottom: 1.25rem;
}

p.subtitle {
  text-align: center;
  font-size: 1rem;
  margin: 0 0 2.5rem 0;
}

div.products {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;

  /* The vertical rule between columns lives on the right edge of `.option:first-child`
     so it stretches to the natural row height of whichever column is taller. */
  margin: 0 0 2rem 0;
}

div.option {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  row-gap: 0.4rem;

  padding: 0 1.5rem;

  &:first-child {
    border-right: 1px solid rgba(0, 0, 0, 0.55);
  }
}

h2.option-name {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
}

p.price {
  font-size: 1.05rem;
  font-weight: 500;
  margin: 0;
}

p.cadence {
  font-size: 1rem;
  font-style: italic;
  margin: 0;

  &.note {
    /* The "(jederzeit kündbar)" line sits flush under "monatlich" with no
       visual gap, matching the reference design. */
    margin-top: -0.25rem;
  }
}

/* The .next button exports `margin: 0 auto`, so it self-centers. We just
   give it a top gap inside the option column. */
div.option .button.next {
  margin-top: 1.5rem;
}

p.error {
  text-align: center;
  color: rgb(180, 30, 30);
  font-size: 0.95rem;
  margin: 0 0 1rem 0;
}

h2.restore-prompt {
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 1rem 0 1.25rem 0;
}

div.restore-row {
  display: flex;
  justify-content: center;
}

button.restore {
  appearance: none;
  background: transparent;
  border: 1.5px solid rgba(0, 142, 207, 1);
  border-radius: 15px;
  color: rgba(0, 142, 207, 1);

  font-size: 1rem;
  font-weight: 600;
  text-transform: lowercase;

  padding: 0.75ch 2ch;
  cursor: pointer;

  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    }
  }
}

p.legal {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 0.5rem;

  margin: 1.75rem 0 0 0;

  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);

  span.dot {
    opacity: 0.5;
  }
}

button.legal-link {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;

  font: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}

/* Phone (≤600px — the app's phone↔tablet boundary). Stack the two products as
   discrete cards instead of divider-split columns: on a narrow screen they read
   as distinct tappable options, and the price gets room to breathe. The >600px
   tablet keeps its side-by-side columns with the vertical rule, untouched. */
@media (max-width: 600px) {
  h1.title {
    margin-bottom: 0.75rem;
  }

  p.subtitle {
    margin-bottom: 1.75rem;
  }

  div.products {
    grid-template-columns: 1fr;
    row-gap: 1rem;
    margin-bottom: 1.5rem;
  }

  div.option {
    padding: 1.25rem 1.5rem 1.5rem;

    background: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 15px;

    /* The cards separate the options now, so drop the tablet's vertical rule. */
    &:first-child {
      border-right: none;
    }
  }

  h2.option-name {
    margin-bottom: 0.85rem;
  }

  p.price {
    font-size: 1.4rem;
  }

  div.option .button.next {
    margin-top: 1.25rem;
  }

  h2.restore-prompt {
    font-size: 1rem;
  }
}
</style>

<i18n>
{
  "de": {
    "title": "Du möchtest LingoTalk unbegrenzt nutzen?",
    "subtitle": "Dann kannst du LingoTalk abonnieren oder kaufen:",
    "lifetime-name": "Einmalkauf",
    "lifetime-cadence": "einmalig",
    "lifetime-action": "Jetzt kaufen",
    "monthly-name": "Monatsabo",
    "monthly-cadence": "monatlich",
    "monthly-action": "Jetzt abonnieren",
    "monthly-note": "(jederzeit kündbar)",
    "restore-prompt": "Du hast LingoTalk bereits auf einem anderen Gerät installiert?",
    "restore-action": "kauf wiederherstellen",
    "privacy": "Datenschutz",
    "terms": "AGB",
    "not-available-on-web": "Käufe sind nur in der iOS- und Android-App verfügbar."
  },
  "en": {
    "title": "Want unlimited LingoTalk?",
    "subtitle": "You can subscribe to or buy LingoTalk:",
    "lifetime-name": "One-time purchase",
    "lifetime-cadence": "once",
    "lifetime-action": "Buy now",
    "monthly-name": "Monthly subscription",
    "monthly-cadence": "per month",
    "monthly-action": "Subscribe now",
    "monthly-note": "(cancel anytime)",
    "restore-prompt": "Already installed LingoTalk on another device?",
    "restore-action": "restore purchase",
    "privacy": "Privacy",
    "terms": "Terms",
    "not-available-on-web": "Purchases are only available in the iOS and Android apps."
  }
}
</i18n>
